import {AnyFunction, logger} from 'yougile-util'

type TimeoutHandler = ReturnType<typeof setTimeout>

export class Debouncer<T> {
  public onReadyHandler?: (data: T[]) => void = undefined
  public timeoutMs = 1000

  private latestRun?: number
  private waitingTasks: Array<() => void> = []
  private waitingData: T[] = []
  private timeoutHandlerId?: TimeoutHandler = undefined

  constructor(timeoutMs: number) {
    this.timeoutMs = timeoutMs
  }

  public millisSinceLatestRun(): number {
    return Date.now() - (this.latestRun ?? 0)
  }

  public clear(): void {
    this.waitingTasks = []
    this.timeoutHandlerId && clearTimeout(this.timeoutHandlerId)
    this.timeoutHandlerId = undefined
  }

  /**
   * Set task for .process method.
   */
  public onReady(handler: (data: T[]) => void): void {
    if (this.onReadyHandler) {
      logger.warn('Redefining handler!')
    }
    this.onReadyHandler = handler
  }

  /**
   * .process is used to accumulate data before running task defined by .onReady
   * method.
   */
  public process(data: T): void {
    this.waitingData.push(data)
    if (!this.timeoutHandlerId) {
      this.timeoutHandlerId = setTimeout(() => this.apply(), this.timeoutMs)
    }
  }

  /**
   * Useful for handling frequent events (e.g. resize, scroll, ...).
   * Debouncer will ignore later run attempts if previous attempt
   * hasn't executed yet.
   */
  public runButNotOften(task: () => void): void {
    if (!this.timeoutHandlerId) {
      this.timeoutHandlerId = setTimeout(() => {
        this.timeoutHandlerId = undefined
        task()
      }, this.timeoutMs)
    }
  }

  public runLatest(task: () => void): void {
    if (this.timeoutHandlerId) {
      clearTimeout(this.timeoutHandlerId)
    }
    this.timeoutHandlerId = setTimeout(() => {
      this.timeoutHandlerId = undefined
      task()
    }, this.timeoutMs)
  }

  /**
   * Same as runButNotOften, but without delays.
   */
  public runNowButNotRightAfter(task: () => void): void {
    const now = Date.now()
    if (!this.latestRun || now - this.latestRun > this.timeoutMs) {
      this.latestRun = now
      task()
    }
  }

  private apply(): void {
    this.timeoutHandlerId && clearTimeout(this.timeoutHandlerId)
    this.timeoutHandlerId = undefined

    this.waitingTasks.forEach((task: () => void) => task())
    this.waitingTasks = []

    if (this.waitingData.length > 0) {
      if (!this.onReadyHandler) {
        logger.error('No handler specified for data')
      }
      const data = [...this.waitingData]
      this.onReadyHandler?.(data)
      this.waitingData = []
    }
  }
}

export class FrameDebouncer {
  private handle: ReturnType<typeof requestAnimationFrame> | undefined

  runLatest(fn: AnyFunction): void {
    this.clear()
    this.handle = requestAnimationFrame(() => {
      this.handle = undefined
      fn()
    })
  }

  runButNotOften(fn: AnyFunction): void {
    if (!this.handle) {
      this.handle = requestAnimationFrame(() => {
        this.handle = undefined
        fn()
      })
    }
  }

  everyFrame(fn: (dt: number) => void): void {
    let tPrev = 0
    this.clear()
    const schedule = () => {
      this.handle = requestAnimationFrame(tCurr => {
        const dt = tPrev ? tCurr - tPrev : 0
        tPrev = tCurr
        fn(dt)
        schedule()
      })
    }
    schedule()
  }

  clear(): void {
    this.handle && cancelAnimationFrame(this.handle)
    this.handle = undefined
  }
}
